import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import { attachJob, detachJob, loseJob } from './jobsSlice'
import type { RootState } from '@/store'

export interface ChatMessage {
  id: string
  text: string
  at: number
}

// Хранилища чата на сервере нет — ни ручки, ни таблицы (§8 контракта). Здесь
// живут ТОЛЬКО реплики автора: события прогона приходят из джобы и собираются
// на лету, дублировать их в persist незачем.
interface AiChatState {
  byCourse: Record<number, ChatMessage[]>
}

const initialState: AiChatState = { byCourse: {} }

export const aiChatSlice = createSlice({
  name: 'aiChat',
  initialState,
  reducers: {
    addUserMessage: (state, action: PayloadAction<{ courseId: number; message: ChatMessage }>) => {
      const list = state.byCourse[action.payload.courseId] ?? []
      list.push(action.payload.message)
      state.byCourse[action.payload.courseId] = list
    },
  },
  extraReducers: (builder) => {
    // Срок жизни чата связан с прогоном КОНСТРУКЦИЕЙ: слайс сам слушает и
    // отвязку, и перецепление джобы. Полагаться на то, что потребитель
    // вызовет `detach()` второй строчкой перед каждым `attach()`, — значит
    // однажды (следующий вызывающий `attach` — под-проекты D-F: картинки,
    // правка блоков, перевод) получить в ленте чат от прошлого прогона.
    builder
      .addCase(detachJob, (state, action) => {
        delete state.byCourse[action.payload]
      })
      // Прогон исчез на бэке (404/403): история про него же — но автор об этом
      // узнаёт из плашки `JobLostNotice`, а не по молча опустевшей ленте.
      .addCase(loseJob, (state, action) => {
        delete state.byCourse[action.payload]
      })
      .addCase(attachJob, (state, action) => {
        // Исключение из инварианта, и ровно два законных вызывающих: цепочка
        // «перевод → картинки» (§7.4, `useTranslationChain`) и явное
        // продолжение прерванного прогона (§8, `useResumeJob`) — оба это ТОТ
        // ЖЕ разговор, продолженный сервером, просто с разных сторон. Везде
        // ещё перецепление означает другой прогон и стирает историю.
        if (action.payload.continuesChat) return
        // Перецепили курс на ДРУГУЮ джобу (свою следующую или чужую, после
        // «Показать прогон») — история прошлого прогона недействительна:
        // её реплики и события принадлежат другому job_id.
        delete state.byCourse[action.payload.courseId]
      })
  },
})

export const { addUserMessage } = aiChatSlice.actions
export const aiChatReducer = aiChatSlice.reducer

// Один и тот же пустой массив на все курсы без истории: литерал `[]` в теле
// селектора создавался заново на каждый вызов, useSelector считал результат
// изменившимся и перерисовывал панель (плюс предупреждение Redux в консоли).
const NO_MESSAGES: ChatMessage[] = []

export const selectChat = (state: RootState, courseId: number): ChatMessage[] =>
  state.aiChat.byCourse[courseId] ?? NO_MESSAGES
